import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, X, Loader2, Clock } from 'lucide-react';
import { api } from '../../App';
import { toast } from 'sonner';
import { stripeErrorToast } from '../../utils/stripeErrors';

const BANNER_DISMISSED_KEY = 'zuriya_stripe_banner_dismissed_session';

/**
 * Persistent top-of-dashboard warning banner for providers who haven't
 * completed Stripe Connect onboarding. Disappears the moment payouts are
 * enabled. Users can dismiss it for the current session (not persisted across
 * logins, we want them to see it until they actually connect).
 *
 * Only shows for approved providers; non-approved providers already have a
 * `StatusBanner` telling them to finish profile approval first.
 */
export function StripeConnectBanner({ providerStatus, onClick }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem(BANNER_DISMISSED_KEY) === '1'; } catch { return false; }
  });

  useEffect(() => {
    if (providerStatus !== 'approved') { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/providers/me/stripe-connect/status');
        if (!cancelled) setStatus(res.data);
      } catch (e) { /* non-fatal, just don't show the banner */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [providerStatus]);

  const handleConnect = async () => {
    setStarting(true);
    try {
      const res = await api.post('/providers/me/stripe-connect/onboard');
      if (res.data?.url) { window.location.href = res.data.url; return; }
    } catch (err) {
      toast.error(stripeErrorToast(err, 'Could not start onboarding'));
    } finally {
      setStarting(false);
    }
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem(BANNER_DISMISSED_KEY, '1'); } catch { /* noop */ }
    setDismissed(true);
  };

  if (loading || dismissed || providerStatus !== 'approved' || !status) return null;
  if (status.status === 'enabled') return null;

  const isPending = status.status === 'pending';
  const tone = isPending
    ? 'border-amber-300 bg-amber-50 text-amber-900'
    : 'border-red-300 bg-red-50 text-red-900';
  const Icon = isPending ? Clock : AlertTriangle;

  const heading = isPending
    ? 'Stripe is verifying your details, payouts will start once approved.'
    : 'You must connect your bank account to receive payouts.';
  const body = isPending
    ? 'This usually takes 1–2 working days. You can keep taking bookings, your earnings are held safely until verification completes.'
    : 'Zuriya holds your earnings safely. They\'ll be transferred to your bank once you finish Stripe\'s 2-minute verification.';

  return (
    <div
      className={`flex items-start gap-3 rounded-xl border ${tone} px-4 py-3 mb-5`}
      data-testid="stripe-connect-banner"
      data-status={status.status}
    >
      <Icon className="h-5 w-5 shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium">{heading}</p>
        <p className="text-xs opacity-80 mt-0.5">{body}</p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {!isPending && (
          <button
            onClick={onClick ? onClick : handleConnect}
            disabled={starting}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A1A1A] text-white text-xs font-medium px-3 py-1.5 hover:opacity-90 transition-opacity disabled:opacity-60"
            data-testid="stripe-connect-banner-cta"
          >
            {onClick ? (
              <>
                <ExternalLink className="h-3.5 w-3.5" />
                Connect now
              </>
            ) : starting ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Opening Stripe...
              </>
            ) : (
              <>
                <ExternalLink className="h-3.5 w-3.5" />
                Connect now
              </>
            )}
          </button>
        )}
        <button
          onClick={handleDismiss}
          className="opacity-60 hover:opacity-100 p-1"
          aria-label="Dismiss"
          data-testid="stripe-connect-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
